import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiClient } from '../api/client';
import { useApi } from './provider';

export const commentKeys = {
  byTask: (workspaceSlug: string, taskUlid: string) =>
    ['comments', 'task', workspaceSlug, taskUlid] as const,
  byNote: (workspaceSlug: string, noteUlid: string) =>
    ['comments', 'note', workspaceSlug, noteUlid] as const,
};

export const taskCommentsQueryOptions = (
  api: ApiClient,
  workspaceSlug: string,
  taskUlid: string,
) =>
  queryOptions({
    queryKey: commentKeys.byTask(workspaceSlug, taskUlid),
    queryFn: async () => {
      const { data, error } = await api.GET(
        '/v1/workspaces/{workspace_slug}/tasks/{taskUlid}/comments',
        {
          params: {
            path: { workspace_slug: workspaceSlug, taskUlid },
          },
        },
      );
      if (error) throw error;
      return data;
    },
  });

export const useTaskComments = (
  workspaceSlug: string | undefined,
  taskUlid: string | undefined,
) => {
  const api = useApi();
  return useQuery({
    ...taskCommentsQueryOptions(api, workspaceSlug ?? '', taskUlid ?? ''),
    enabled: Boolean(workspaceSlug && taskUlid),
  });
};

export const noteCommentsQueryOptions = (
  api: ApiClient,
  workspaceSlug: string,
  noteUlid: string,
) =>
  queryOptions({
    queryKey: commentKeys.byNote(workspaceSlug, noteUlid),
    queryFn: async () => {
      const { data, error } = await api.GET(
        '/v1/workspaces/{workspace_slug}/notes/{noteUlid}/comments',
        {
          params: {
            path: { workspace_slug: workspaceSlug, noteUlid },
          },
        },
      );
      if (error) throw error;
      return data;
    },
  });

export const useNoteComments = (
  workspaceSlug: string | undefined,
  noteUlid: string | undefined,
) => {
  const api = useApi();
  return useQuery({
    ...noteCommentsQueryOptions(api, workspaceSlug ?? '', noteUlid ?? ''),
    enabled: Boolean(workspaceSlug && noteUlid),
  });
};

export interface CreateCommentInput {
  body: string;
}

export const useCreateTaskComment = (workspaceSlug: string, taskUlid: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateCommentInput) => {
      const { data, error } = await api.POST(
        '/v1/workspaces/{workspace_slug}/tasks/{taskUlid}/comments',
        {
          params: { path: { workspace_slug: workspaceSlug, taskUlid } },
          body: input,
        },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: commentKeys.byTask(workspaceSlug, taskUlid) });
    },
  });
};

export const useCreateNoteComment = (workspaceSlug: string, noteUlid: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateCommentInput) => {
      const { data, error } = await api.POST(
        '/v1/workspaces/{workspace_slug}/notes/{noteUlid}/comments',
        {
          params: { path: { workspace_slug: workspaceSlug, noteUlid } },
          body: input,
        },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: commentKeys.byNote(workspaceSlug, noteUlid) });
    },
  });
};

export interface UpdateCommentInput {
  body: string;
}

/**
 * Update a comment's body. Per the locked pattern, the target
 * `commentId` is passed in the mutate input rather than at hook-factory
 * time — same rationale as `useUpdateNote` and `useReorderTask` (avoids
 * stale-closure bugs when callers swap targets between renders, which
 * a comment thread does as the user clicks Edit on different rows).
 */
export const useUpdateComment = (workspaceSlug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateCommentInput & { commentId: string }) => {
      const { commentId, ...body } = input;
      const { data, error } = await api.PATCH(
        '/v1/workspaces/{workspace_slug}/comments/{id}',
        {
          params: { path: { workspace_slug: workspaceSlug, id: commentId } },
          body,
        },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      // Both threads can be affected — we don't carry the parent
      // type/id from the mutation input, so invalidate both families.
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['comments', 'task', workspaceSlug] }),
        qc.invalidateQueries({ queryKey: ['comments', 'note', workspaceSlug] }),
      ]);
    },
  });
};

export const useDeleteComment = (workspaceSlug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (commentId: string) => {
      const { error } = await api.DELETE(
        '/v1/workspaces/{workspace_slug}/comments/{id}',
        {
          params: { path: { workspace_slug: workspaceSlug, id: commentId } },
        },
      );
      if (error) throw error;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['comments', 'task', workspaceSlug] }),
        qc.invalidateQueries({ queryKey: ['comments', 'note', workspaceSlug] }),
      ]);
    },
  });
};
